/**
 * NsectionB2Page
 * B.2.適用範圍與生效（A6.4-FORM-METH-002 Section 2）
 * 三頁籤：B.2.1 適用範圍 / B.2.2 生效日期 / B.2.3 適用部門範疇
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB2Service, { NsectionB2Detail, NsectionB2Update } from '../services/nsectionB2Service';
import { getSystemCodesByType, SystemCode } from '../services/systemCodeService';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';

const NsectionB2Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId, selectedProposal } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB2Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<1 | 2 | 3>(1);
  const [sectoralScopeOptions, setSectoralScopeOptions] = useState<SystemCode[]>([]);

  const [formData, setFormData] = useState({
    scope_description: '',
    entry_into_force: '',
    sectoral_scopes: [] as number[],
    sectoral_scopes_text: '',
  });

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NsectionB2Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    getSystemCodesByType('Sectoral Scopes').then(setSectoralScopeOptions).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedProposalId) {
      setData(null);
      setFormData({ scope_description: '', entry_into_force: '', sectoral_scopes: [], sectoral_scopes_text: '' });
      return;
    }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const result = await nsectionB2Service.getByProposal(selectedProposalId);
        if (result) {
          setData(result);
          setFormData({
            scope_description: result.scope_description || '',
            entry_into_force: result.entry_into_force || '',
            sectoral_scopes: result.sectoral_scopes || [],
            sectoral_scopes_text: result.sectoral_scopes_text || '',
          });
        } else {
          setData(null);
          setFormData({
            scope_description: '', entry_into_force: '',
            sectoral_scopes: selectedProposal?.sectoral_scopes || [],
            sectoral_scopes_text: '',
          });
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, selectedProposal, isZh]);

  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      let updateData: NsectionB2Update = {};
      if (activeTab === 1) {
        updateData = { scope_description: formData.scope_description || undefined };
      } else if (activeTab === 2) {
        updateData = { entry_into_force: formData.entry_into_force || undefined };
      } else if (activeTab === 3) {
        updateData = {
          sectoral_scopes: formData.sectoral_scopes,
          sectoral_scopes_text: formData.sectoral_scopes_text || undefined,
        };
      }
      const result = await nsectionB2Service.update(selectedProposalId, updateData);
      setData(result);
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally { setLoading(false); }
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b2', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canUpdate = hasPermission('nsection_b2', 'update');

  const cardStyle: React.CSSProperties = {
    width: '100%', background: '#fff', borderRadius: '10px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px',
  };
  const cardTitleStyle: React.CSSProperties = { fontSize: '16px', fontWeight: 700, color: '#333', marginBottom: '6px' };
  const hintStyle: React.CSSProperties = { fontSize: '13px', color: '#666', marginBottom: '20px', padding: '10px 14px', background: '#f9f9f9', borderRadius: '6px' };

  const tabs = [
    { key: 1 as const, zh: 'B.2.1.適用範圍', en: 'B.2.1. Scope' },
    { key: 2 as const, zh: 'B.2.2.生效日期', en: 'B.2.2. Entry into force' },
    { key: 3 as const, zh: 'B.2.3.適用部門範疇', en: 'B.2.3. Applicability of sectoral scopes' },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="nsection_b2" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div>
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '20px' }}>
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                padding: '10px 24px', border: 'none', cursor: 'pointer', fontSize: '14px', marginBottom: '-2px',
                borderBottom: activeTab === tab.key ? '3px solid #4A90E2' : '3px solid transparent',
                background: activeTab === tab.key ? '#f0f7ff' : 'transparent',
                color: activeTab === tab.key ? '#4A90E2' : '#666', fontWeight: activeTab === tab.key ? 600 : 400,
              }}>
                {isZh ? tab.zh : tab.en}
              </button>
            ))}
          </div>

          {/* Tab 1: B.2.1 適用範圍 */}
          {activeTab === 1 && (
            <div style={cardStyle}>
              <div style={cardTitleStyle}>{isZh ? 'B.2.1. 適用範圍' : 'B.2.1. Scope'}</div>
              <div style={hintStyle}>
                {isZh ? '適用範圍明確定義本文件涵蓋的範圍。' : 'The scope defines without ambiguity the extent or ranges covered by the document.'}
              </div>
              <RichTextEditor value={formData.scope_description}
                onChange={(html) => setFormData({ ...formData, scope_description: html })}
                disabled={!canUpdate} placeholder={isZh ? '請輸入適用範圍說明...' : 'Enter scope description...'} />
              {canUpdate && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '20px', borderTop: '1px solid #f0f0f0', marginTop: '20px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                    {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: B.2.2 生效日期 */}
          {activeTab === 2 && (
            <div style={cardStyle}>
              <div style={cardTitleStyle}>{isZh ? 'B.2.2. 生效日期' : 'B.2.2. Entry into force'}</div>
              <div style={hintStyle}>
                {isZh ? '由 UNFCCC 秘書處填寫 - 留空' : 'For the UNFCCC secretariat to complete - Leave blank'}
              </div>
              <RichTextEditor value={formData.entry_into_force}
                onChange={(html) => setFormData({ ...formData, entry_into_force: html })}
                disabled={!canUpdate} placeholder={isZh ? '由秘書處填寫...' : 'To be completed by the secretariat...'} />
              {canUpdate && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '20px', borderTop: '1px solid #f0f0f0', marginTop: '20px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                    {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab 3: B.2.3 適用部門範疇 */}
          {activeTab === 3 && (
            <div style={cardStyle}>
              <div style={cardTitleStyle}>{isZh ? 'B.2.3. 適用部門範疇' : 'B.2.3. Applicability of sectoral scopes'}</div>
              <div style={hintStyle}>
                {isZh
                  ? '使用本方法論驗證及查證 Article 6.4 活動的指定作業實體(DOE)應適用之部門範疇：'
                  : 'Designated operational entities validating and verifying Article 6.4 activities that use this methodology shall apply sectoral scope(s):'}
              </div>

              {/* 部門範疇多選 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
                  {isZh ? '適用部門範疇' : 'Sectoral Scope(s)'}
                </label>
                <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px', maxHeight: '240px', overflowY: 'auto' }}>
                  {sectoralScopeOptions.map(s => (
                    <label key={s.id} style={{ display: 'block', marginBottom: '6px', fontSize: '13px', cursor: canUpdate ? 'pointer' : 'default' }}>
                      <input type="checkbox" disabled={!canUpdate}
                        checked={formData.sectoral_scopes.includes(s.id)}
                        onChange={(e) => {
                          const scopes = e.target.checked
                            ? [...formData.sectoral_scopes, s.id]
                            : formData.sectoral_scopes.filter(id => id !== s.id);
                          setFormData({ ...formData, sectoral_scopes: scopes });
                        }}
                        style={{ marginRight: '8px' }} />
                      {isZh ? (s.code_cname || s.code_ename) : (s.code_ename || s.code_cname)}
                    </label>
                  ))}
                </div>
                <small style={{ color: '#999', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {isZh ? '預設從方法論提案帶入' : 'Default from methodology proposal'}
                </small>
              </div>

              {/* 部門範疇說明 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
                  {isZh ? '適用部門範疇說明' : 'Sectoral Scopes Description'}
                </label>
                <RichTextEditor value={formData.sectoral_scopes_text}
                  onChange={(html) => setFormData({ ...formData, sectoral_scopes_text: html })}
                  disabled={!canUpdate} placeholder={isZh ? '請輸入部門範疇說明...' : 'Enter sectoral scopes description...'} />
              </div>

              {canUpdate && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '20px', borderTop: '1px solid #f0f0f0' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                    {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NsectionB2Page;
